import type { HTMLAttributes, RefObject } from 'react';
import { useCallback, useMemo, useRef } from 'react';

import { Chart } from '@highcharts/react';
import { StockChart } from '@highcharts/react/Stock';

import { useChartContext } from '@/components/Chart/context/ChartContext';
import { mergeChartEvents } from '@/components/Chart/hooks/options/useChartFunctions';
import type { ExtendedOptions } from '@/components/Chart/types/highcharts';
import { Highcharts } from '@/lib/highcharts';

import type { HighchartsReactRefObject } from '@highcharts/react/Stock';
import type HighchartsType from 'highcharts';

type ChartShellProps = {
  options: ExtendedOptions | undefined;
  isStockChart?: boolean;
  chartId?: string;
  containerProps?: HTMLAttributes<HTMLDivElement>;
  chartComponentRef?: RefObject<HighchartsReactRefObject | null>;
  onChartReady?: (chart: HighchartsType.Chart) => void;
};

const ChartShell = ({
  options,
  isStockChart = false,
  chartId,
  containerProps,
  chartComponentRef,
  onChartReady
}: ChartShellProps) => {
  const internalRef = useRef<HighchartsReactRefObject | null>(null);
  const chartRefToUse = chartComponentRef ?? internalRef;
  const { chartRef } = useChartContext();

  const handleChartReady = useCallback(
    function (this: HighchartsType.Chart) {
      if (chartRefToUse.current) {
        chartRef.current = chartRefToUse.current;
      } else {
        chartRef.current = { chart: this };
      }
      if (onChartReady) onChartReady(this);
    },
    [chartRef, chartRefToUse, onChartReady]
  );

  const mergedOptions = useMemo(() => {
    if (!options) return options;
    return {
      ...options,
      chart: {
        ...options.chart,
        events: mergeChartEvents(options.chart?.events, { load: handleChartReady })
      }
    };
  }, [options, handleChartReady]);

  if (!mergedOptions) return null;

  const Component = isStockChart ? StockChart : Chart;

  return (
    <Component
      key={chartId}
      ref={chartRefToUse}
      containerProps={containerProps}
      highcharts={Highcharts as any}
      options={mergedOptions as any}
    />
  );
};

export default ChartShell;
